
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import ReactFlow, {
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Handle,
    Position,
    MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useParams, useNavigate } from "react-router";
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

// Hooks
import useAuth from "src/hooks/useAuth";

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from "src/@/components/ui/card";
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import Iconify from 'src/components/Iconify';
import { ScrollArea } from "src/@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/@/components/ui/select";
import { Switch } from "src/@/components/ui/switch";
import { Badge } from "src/@/components/ui/badge";
import ModalConfimacao from "src/components/modal/ModalConfimacao";
import { DialogLogsAutomacao } from "src/sections/@dashboard/automacoes/DialogLogsAutomacao";
import { fDateTime } from "src/utils/formatTime";

// Config & Actions
import { useAutomationActions, useAutomationEvents } from "src/config";
import { isStepIncomplete } from "./stepValidation";
import v2_actionAutomacao from "src/actions/v2/automacoes/v2_actionAutomacao";
import v2_actionEditarAutomacao from "src/actions/v2/automacoes/v2_actionEditarAutomacao";
import v2_actionDeletarAutomacao from "src/actions/v2/automacoes/v2_actionDeletarAutomacao";
import actionListaCanaisPorCliente from "src/actions/clientes/actionListaCanaisPorCliente";
import getActionListaUsuariosByClienteId from "src/actions/usuarios/getActionListaUsuariosByClienteId";

// --- TRIGGER COMPONENTS ---
import CardActionSairEstagio from "src/sections/@dashboard/automacoes/action/CardActionSairEstagio";
import ChatAccept from "src/sections/@dashboard/automacoes/action/ChatAccept";
import ChatAssign from "src/sections/@dashboard/automacoes/action/ChatAssign";
import CardActionInOutFase from "src/sections/@dashboard/automacoes/action/CardActionInOutFase";
import CardInatividadeFunil from "src/sections/@dashboard/automacoes/action/CardInatividadeFunil";
import CardInatividadeChat from "src/sections/@dashboard/automacoes/action/CardInatividadeChat";
import ChatKeywordFilter from "src/sections/@dashboard/automacoes/action/ChatKeywordFilter";
import CardActionCampoAtualizado from "src/sections/@dashboard/automacoes/action/CardActionCampoAtualizado";
import CardActionEntrarFase from "src/sections/@dashboard/automacoes/action/CardActionEntrarFase";
import CardChatTagInOut from "src/sections/@dashboard/automacoes/action/CardChatTagInOut";
import CardChatFeedback from "src/sections/@dashboard/automacoes/action/CardChatFeedback";
import CardChatFinish from "src/sections/@dashboard/automacoes/action/CardChatFinish";
import CardResumoConversa from "src/sections/@dashboard/automacoes/action/CardResumoConversa";
import CardChatFirstMessage from "src/sections/@dashboard/automacoes/action/CardChatFirstMessage";
import { LabelFromAction } from "src/components/@dashboard/automacoes";

// --- REACTION (ACTION) COMPONENTS ---
import CardSendMake from "src/sections/@dashboard/automacoes/reaction/CardSendMake";
import ChatAddTag from "src/sections/@dashboard/automacoes/reaction/ChatAddTag";
import CardNewLead from "src/sections/@dashboard/automacoes/reaction/CardNewLead";
import CardChangeValue from "src/sections/@dashboard/automacoes/reaction/CardChangeValue";
import ChatAddNoteComponent from "src/sections/@dashboard/automacoes/reaction/ChatAddNoteComponent";
import CardSendMessage from "src/sections/@dashboard/automacoes/reaction/CardSendMessage";
import CardTransferCard from "src/sections/@dashboard/automacoes/reaction/CardTransferCard";
import HttpRequestComponent from "src/sections/@dashboard/automacoes/reaction/HttpRequestComponent";
import CardReactionMoverCardFunil from "src/sections/@dashboard/automacoes/reaction/CardReactionMoverCardFunil";
import CardFinishChat from "src/sections/@dashboard/automacoes/reaction/CardFinishChat";
import CardEnableBot from "src/sections/@dashboard/automacoes/reaction/CardEnableBot";
import CardTransferChat from "src/sections/@dashboard/automacoes/reaction/CardTransferChat";
import ChatRemoveTag from "src/sections/@dashboard/automacoes/reaction/ChatRemoveTag";
import CardAssignChat from "src/sections/@dashboard/automacoes/reaction/CardAssignChat";
import CardCondition from "src/sections/@dashboard/automacoes/reaction/CardCondition";
import CardWait from "src/sections/@dashboard/automacoes/reaction/CardWait";
import BlockRenderer from "src/sections/@dashboard/automacoes/reaction/BlockRenderer";
import { AUTOMACOES_DESIGN as D } from "./Automacoes.design";

const diasSemana = [
    { dia: "Domingo", numeroDia: 0 },
    { dia: "Segunda-feira", numeroDia: 1 },
    { dia: "Terça-feira", numeroDia: 2 },
    { dia: "Quarta-feira", numeroDia: 3 },
    { dia: "Quinta-feira", numeroDia: 4 },
    { dia: "Sexta-feira", numeroDia: 5 },
    { dia: "Sábado", numeroDia: 6 },
];

const checkAction = (action, state) => {
    if (!action || !state) return false;

    const rules = {
        "from.funnel.card.field.change": () => state.pipelineId !== null && state.stageId !== null && Array.isArray(state.valuesFields) && state.valuesFields.length > 0,
        "from.funnel.card.create": () => true,
        "from.jobs.birthdate": () => true,
        "from.chat.firstMessage": () => Array.isArray(state.chatChannels) && state.chatChannels.length > 0,
        "from.chat.finish": () => true,
        "from.chat.tag.inOut": () => Array.isArray(state.tags) && state.tags.length > 0,
        "from.chat.contains": () => (state.detectTypeMsg === true && state.detectTypeMsgTypes && state.detectTypeMsgTypes.length > 0) || (state.aiDetect === true && state.aiDetectContext && state.aiDetectContext.trim() !== "") || ((Array.isArray(state.chatKeywords) && state.chatKeywords.length > 0) && state.chatKeywords.length > 0 && (Array.isArray(state.chatStatus) && state.chatStatus.length > 0) && state.chatStatus.length > 0),
        "from.chat.feedback": () => (Array.isArray(state.feedbacks) && state.feedbacks.length > 0),
        "from.funnel.card.out.stage": () => state.pipelineId !== null && state.stageId !== null,
        "from.funnel.card.in.stage": () => state.pipelineId !== null && state.stageId !== null,
        "from.funnel.card.inOut.stage": () => state.pipelineId !== null && state.stageId !== null && state.stageIdTarget !== null,
        "from.chat.accept": () => Array.isArray(state.chatUsersAccept) && state.chatUsersAccept.length > 0,
        "from.chat.assign": () => (Array.isArray(state.chatUsersAssign) && state.chatUsersAssign.length > 0) || (Array.isArray(state.chatDepartamentsAssign) && state.chatDepartamentsAssign.length > 0),
        "from.chat.inactivity": () => (Array.isArray(state.chatStatus) && state.chatStatus.length > 0) && state.chatStatus.length > 0 && state.timeInativity !== null,
        "from.funnel.card.inativity": () => state.pipelineId !== null && (Array.isArray(state.stage_ids) && state.stage_ids.length > 0) && state.stage_ids.length > 0,
        "from.chats.resume": () => state.timeResume !== null && (Array.isArray(state.chatStatus) && state.chatStatus.length > 0) && state.chatStatus.length > 0 && state.chatClientId !== null
    };

    if (!rules[action]) return false;
    return rules[action]();
};

// Custom Node
const N8nNode = ({ data, isConnectable }) => {
    const { t } = useTranslation('automation');
    return (
        <div className="relative group pt-5">
            {/* Icon on border */}
            <div className={`
                absolute -top-0 left-1/2 -translate-x-1/2 z-10
                w-10 h-10 rounded-xl flex items-center justify-center
                shadow-md border-2 border-white dark:border-[#222529]
                ${data.colorClass || 'bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 text-[#7C3AED]'}
            `}>
                <Iconify icon={data.icon || 'mdi:lightning-bolt'} width={20} height={20} />
            </div>

            <div className={`
                shadow-sm hover:shadow-xl transition-all duration-300
                rounded-xl bg-white dark:bg-[#222529] min-w-[240px] max-w-[240px]
                border border-gray-100 dark:border-[#383838] group-hover:border-[#7C3AED]/50
                ${data.isIncomplete ? '!border-red-400 ring-2 ring-red-300/70 shadow-md shadow-red-200/50' : ''}
            `}>
                {/* Header Section */}
                <div className={`
                    flex flex-col items-center pt-7 pb-3 px-3 rounded-t-xl border-b border-dashed border-gray-100 dark:border-[#383838] relative
                    ${data.isTrigger ? 'bg-gradient-to-b from-emerald-50/80 to-white dark:from-emerald-500/10 dark:to-[#222529]' : 'bg-white dark:bg-[#222529]'}
                `}>
                    <span className="font-bold text-[11px] text-[#111827] dark:text-[#D1D2D3] line-clamp-2 leading-snug text-center w-full break-words">
                        {data.label}
                    </span>
                    <span className="text-[9px] font-bold text-[#6B7280] dark:text-[#ABABAD] uppercase tracking-widest mt-0.5">
                        {data.subLabel}
                    </span>

                    {(!data.isTrigger || data.onDelete) && (
                        <button
                            className="absolute right-2 top-2 p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-300 dark:text-[#ABABAD] hover:text-red-500 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                                e.stopPropagation();
                                data.onDelete?.();
                            }}
                        >
                            <Iconify icon="mdi:trash-can-outline" width={14} height={14} />
                        </button>
                    )}
                </div>

                {/* Body/Description Section */}
                <div className="p-2 bg-[#F9FAFB]/50 dark:bg-[#1A1D21]/50 rounded-b-xl min-h-[36px] flex items-center justify-center">
                    <div className="text-[10px] text-[#6B7280] dark:text-[#ABABAD] line-clamp-2 leading-relaxed px-1 w-full font-medium text-center">
                        {data.description || t('workflow.node.noConfig', 'Sem configuração definida')}
                    </div>
                </div>
            </div>

            {/* Connection Handles */}
            {!data.isTrigger && (
                <Handle
                    type="target"
                    position={Position.Top}
                    isConnectable={isConnectable}
                    className="
                        !w-3.5 !h-3.5 !bg-white dark:!bg-[#222529] !border-[3px] !border-gray-300 dark:!border-[#383838]
                        group-hover:!border-[#7C3AED] transition-colors
                        !top-[11px] shadow-sm
                    "
                />
            )}
            <Handle
                type="source"
                position={Position.Bottom}
                isConnectable={isConnectable}
                className="
                    !w-3.5 !h-3.5 !bg-white dark:!bg-[#222529] !border-[3px] !border-gray-300 dark:!border-[#383838]
                     group-hover:!border-[#7C3AED] transition-colors
                    !-bottom-[9px] shadow-sm
                "
            />
        </div>
    );
};

// Condition Node - two source handles for branching
const ConditionNode = ({ data, isConnectable }) => {
    const { t } = useTranslation('automation');
    return (
        <div className="relative group pt-5">
            {/* Icon on border */}
            <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10 w-10 h-10 rounded-xl flex items-center justify-center shadow-md border-2 border-white dark:border-[#222529] bg-amber-100 dark:bg-amber-500/20 text-amber-600">
                <Iconify icon={data.icon || 'mdi:source-branch'} width={20} height={20} />
            </div>

            <div className={`
                shadow-sm hover:shadow-xl transition-all duration-300
                rounded-xl bg-white dark:bg-[#222529] min-w-[240px] max-w-[240px]
                border border-gray-100 dark:border-[#383838] group-hover:border-[#7C3AED]/50
                ${data.isIncomplete ? '!border-red-400 ring-2 ring-red-300/70 shadow-md shadow-red-200/50' : ''}
            `}>
                {/* Header Section */}
                <div className="flex flex-col items-center pt-7 pb-3 px-3 rounded-t-xl border-b border-dashed border-gray-100 dark:border-[#383838] relative bg-white dark:bg-[#222529]">
                    <span className="font-bold text-[11px] text-[#111827] dark:text-[#D1D2D3] line-clamp-2 leading-snug text-center w-full break-words">
                        {data.label}
                    </span>
                    <span className="text-[9px] font-bold text-[#6B7280] dark:text-[#ABABAD] uppercase tracking-widest mt-0.5">
                        {data.subLabel}
                    </span>
                    <button
                        className="absolute right-2 top-2 p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-300 dark:text-[#ABABAD] hover:text-red-500 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onDelete?.();
                        }}
                    >
                        <Iconify icon="mdi:trash-can-outline" width={14} height={14} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-2 bg-[#F9FAFB]/50 dark:bg-[#1A1D21]/50 min-h-[36px] flex items-center justify-center">
                    <div className="text-[10px] text-[#6B7280] dark:text-[#ABABAD] line-clamp-2 leading-relaxed px-1 w-full font-medium text-center">
                        {data.description || t('workflow.node.noConfig', 'Sem configuração definida')}
                    </div>
                </div>

                {/* Branch handles area */}
                <div className="relative h-10 bg-transparent rounded-b-xl flex items-center justify-between px-6">
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-[#222529] shadow-sm" />
                        <span className="text-[9px] font-bold text-green-600">{t('workflow.branch.true', 'Se')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-[9px] font-bold text-red-600">{t('workflow.branch.false', 'Senão')}</span>
                        <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-[#222529] shadow-sm" />
                    </div>
                </div>
            </div>

            {/* Target Handle (top) */}
            <Handle
                type="target"
                position={Position.Top}
                isConnectable={isConnectable}
                className="!w-3.5 !h-3.5 !bg-white dark:!bg-[#222529] !border-[3px] !border-gray-300 dark:!border-[#383838] group-hover:!border-[#7C3AED] transition-colors !top-[11px] shadow-sm"
            />
            {/* True Source Handle (left) */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="true-out"
                isConnectable={isConnectable}
                className="!w-3 !h-3 !bg-green-500 !border-[2px] !border-white dark:!border-[#222529] shadow-sm !opacity-0"
                style={{ left: '15%' }}
            />
            {/* False Source Handle (right) */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="false-out"
                isConnectable={isConnectable}
                className="!w-3 !h-3 !bg-red-500 !border-[2px] !border-white dark:!border-[#222529] shadow-sm !opacity-0"
                style={{ left: '85%' }}
            />
        </div>
    );
};

// Converge Node - where branches reconnect
const ConvergeNode = ({ isConnectable }) => {
    return (
        <div className="relative">
            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#383838] border-2 border-gray-300 dark:border-[#4a4a4a]" />
            <Handle
                type="target"
                position={Position.Top}
                isConnectable={isConnectable}
                className="!w-2 !h-2 !bg-gray-300 dark:!bg-[#4a4a4a] !border-0 !-top-[5px]"
            />
            <Handle
                type="source"
                position={Position.Bottom}
                isConnectable={isConnectable}
                className="!w-2 !h-2 !bg-gray-300 dark:!bg-[#4a4a4a] !border-0 !-bottom-[5px]"
            />
        </div>
    );
};

// Add Button Node
const AddNode = ({ data, isConnectable, onClick }) => {
    const accentColor = data?.accentColor;
    let borderClass = 'border-gray-300 dark:border-[#383838] hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-500/10';
    let iconClass = 'text-gray-400 dark:text-[#ABABAD] group-hover:text-violet-600';

    if (accentColor === 'green') {
        borderClass = 'border-green-300 dark:border-green-700 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-500/10';
        iconClass = 'text-green-400 dark:text-green-600 group-hover:text-green-600';
    } else if (accentColor === 'red') {
        borderClass = 'border-red-300 dark:border-red-700 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-500/10';
        iconClass = 'text-red-400 dark:text-red-600 group-hover:text-red-600';
    }

    return (
        <div
            onClick={onClick}
            className={`flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-[#1A1D21] rounded-full border-2 border-dashed ${borderClass} cursor-pointer transition-colors group`}
        >
            <Iconify icon="mdi:plus" className={`${iconClass} text-lg`} />
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} className="opacity-0" />
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} className="opacity-0" />
        </div>
    );
};

const Sidebar = ({ actions, onAddBlock, ...props }) => {
    const { t } = useTranslation('automation');
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(true);

    const searchFiltered = useMemo(() => {
        if (!search) return actions;
        return actions.filter(a =>
            a.name.toLowerCase().includes(search.toLowerCase()) ||
            a.category.toLowerCase().includes(search.toLowerCase())
        );
    }, [actions, search]);

    const grouped = useMemo(() =>
        searchFiltered.reduce((groups, action) => {
            if (!groups[action.category]) groups[action.category] = [];
            groups[action.category].push(action);
            return groups;
        }, {}),
        [searchFiltered]);

    return (
        <div className={`h-full shrink-0 z-20 relative transition-all duration-300 ${isOpen ? 'w-[260px]' : 'w-0'}`}>
            <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    absolute top-3 z-50 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] shadow-md rounded-full w-7 h-7
                    hover:bg-slate-50 dark:hover:bg-[#383838] transition-all duration-300
                    ${isOpen ? '-right-3.5' : '-right-10'}
                `}
            >
                <Iconify
                    icon={isOpen ? "mdi:chevron-left" : "mdi:chevron-right"}
                    className="text-slate-600 dark:text-[#ABABAD]"
                    width={16}
                />
            </Button>

            <div className={`
                w-full h-full bg-white dark:bg-[#222529] border-r border-gray-200 dark:border-[#383838] flex flex-col overflow-hidden transition-all duration-300
                ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}>
                {/* Header */}
                <div className="px-4 pt-4 pb-3">
                    <h2 className="font-bold text-sm mb-3 text-slate-800 dark:text-[#D1D2D3]">{props.title || 'Add Nodes'}</h2>
                    <div className="relative">
                        <Iconify icon="mdi:magnify" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#ABABAD]" width={15} />
                        <input
                            className="w-full pl-8 pr-3 py-2 bg-gray-50 dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all font-medium text-slate-600 dark:text-[#D1D2D3] placeholder:text-slate-400 dark:placeholder:text-[#ABABAD]"
                            placeholder={t('workflow.sidebar.searchPlaceholder', 'Buscar...')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    <div className="px-3 pb-8 space-y-4">
                        {Object.entries(grouped).map(([category, items]) => (
                            <div key={category}>
                                <span className="text-[10px] font-extrabold text-slate-400 dark:text-[#ABABAD] uppercase tracking-widest px-1 mb-1.5 block">{category}</span>
                                <div className="space-y-0.5">
                                    {items.map(action => (
                                        <div
                                            key={action.value}
                                            onClick={() => onAddBlock(action.value)}
                                            className="
                                                group flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                                                hover:bg-violet-50 dark:hover:bg-violet-500/10
                                                cursor-pointer transition-colors duration-150 overflow-hidden min-w-0
                                            "
                                        >
                                            <div className="w-7 h-7 rounded-md bg-gray-50 dark:bg-[#1A1D21] text-slate-400 dark:text-[#ABABAD] flex items-center justify-center shrink-0 border border-gray-100 dark:border-[#383838] group-hover:text-violet-600 group-hover:border-violet-200 dark:group-hover:border-violet-500/30 transition-colors">
                                                <Iconify icon={action.icon} width={15} height={15} />
                                            </div>
                                            <span className="font-medium text-xs text-slate-600 dark:text-[#ABABAD] group-hover:text-violet-700 dark:group-hover:text-violet-400 leading-tight line-clamp-2 flex-1 min-w-0 break-words" title={action.name}>
                                                {action.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {actions.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-10 px-3 text-center text-slate-400 dark:text-[#ABABAD]">
                                <Iconify icon="mdi:information-outline" className="text-2xl mb-2 opacity-50" />
                                <p className="text-xs">{t('workflow.sidebar.emptyState', 'Selecione um gatilho para ver as ações disponíveis.')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Workflow() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { user, cliente } = useAuth();
    const automationActions = useAutomationActions();
    const automationEvents = useAutomationEvents();
    const { t } = useTranslation(['automation', 'common', 'notifications']);

    // React Flow State
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Automation Data State
    const [automacao, setAutomacao] = useState(null);
    const [actionState, setActionState] = useState(null); // The "From" params
    const [blocks, setBlocks] = useState([]); // The "To" params
    const [actionPhase, setActionPhase] = useState(null); // The selected "From" event definition

    // Modal / Configuration State
    const [openConfig, setOpenConfig] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Aux Data
    const [canais, setCanais] = useState([]);
    const [users, setUsers] = useState([]);

    const usersFormatted = useMemo(() => users?.map((u) => ({ value: u._id, label: u.nomeFantasia })), [users]);

    // Derived States
    const filteredActions = useMemo(() =>
        automationActions.filter(action => action.accepts.includes(actionPhase?.value)),
        [actionPhase]);

    const groupedActions = useMemo(() =>
        filteredActions.reduce((groups, action) => {
            if (!groups[action.category]) {
                groups[action.category] = [];
            }
            groups[action.category].push(action);
            return groups;
        }, {}),
        [filteredActions]);

    // Determine Sidebar Content
    const sidebarItems = useMemo(() => actionPhase ? filteredActions : automationEvents, [actionPhase, filteredActions]);
    const sidebarTitle = actionPhase ? t('shared.addAction', 'Adicionar Ação') : t('workflow.selectTrigger', 'Selecionar Gatilho'); // Better visual context

    const isActionValid = useMemo(() => checkAction(actionPhase?.value, actionState), [actionPhase, actionState]);

    // Available variables for variable inputs (HTTP Request, etc)
    const availableVariables = useMemo(() => {
        const vars = [
            // System variables
            { key: 'automacao.id', description: t('variables.automationId', 'ID da automação'), type: t('variables.types.system', 'Sistema') },
            { key: 'automacao.name', description: t('variables.automationName', 'Nome da automação'), type: t('variables.types.system', 'Sistema') },
            { key: 'cliente.id', description: t('variables.clienteId', 'ID do cliente'), type: t('variables.types.system', 'Sistema') },
            // Contact/Chat variables
            { key: 'contato.id', description: t('variables.contatoId', 'ID do contato'), type: t('variables.types.contact', 'Contato') },
            { key: 'contato.nome', description: t('variables.contatoNome', 'Nome do contato'), type: t('variables.types.contact', 'Contato') },
            { key: 'contato.telefone', description: t('variables.contatoTelefone', 'Telefone do contato'), type: t('variables.types.contact', 'Contato') },
            { key: 'contato.email', description: t('variables.contatoEmail', 'Email do contato'), type: t('variables.types.contact', 'Contato') },
            { key: 'chat.id', description: t('variables.chatId', 'ID do chat'), type: t('variables.types.chat', 'Chat') },
            { key: 'chat.status', description: t('variables.chatStatus', 'Status do chat'), type: t('variables.types.chat', 'Chat') },
            { key: 'chat.ultimaMensagem', description: t('variables.chatUltimaMensagem', 'Última mensagem recebida'), type: t('variables.types.chat', 'Chat') },
            { key: 'chat.canal', description: t('variables.chatCanal', 'Canal do chat (WhatsApp, etc)'), type: t('variables.types.chat', 'Chat') },
            // Funnel/Card variables
            { key: 'card.id', description: t('variables.cardId', 'ID do card no funil'), type: t('variables.types.funnel', 'Funil') },
            { key: 'card.titulo', description: t('variables.cardTitulo', 'Título do card'), type: t('variables.types.funnel', 'Funil') },
            { key: 'card.valor', description: t('variables.cardValor', 'Valor do card'), type: t('variables.types.funnel', 'Funil') },
            { key: 'pipeline.id', description: t('variables.pipelineId', 'ID do pipeline'), type: t('variables.types.funnel', 'Funil') },
            { key: 'pipeline.nome', description: t('variables.pipelineNome', 'Nome do pipeline'), type: t('variables.types.funnel', 'Funil') },
            { key: 'stage.id', description: t('variables.stageId', 'ID da etapa'), type: t('variables.types.funnel', 'Funil') },
            { key: 'stage.nome', description: t('variables.stageNome', 'Nome da etapa'), type: t('variables.types.funnel', 'Funil') },
            // User variables
            { key: 'usuario.id', description: t('variables.usuarioId', 'ID do usuário responsável'), type: t('variables.types.user', 'Usuário') },
            { key: 'usuario.nome', description: t('variables.usuarioNome', 'Nome do usuário'), type: t('variables.types.user', 'Usuário') },
        ];

        // Add trigger-specific variables based on actionPhase
        if (actionPhase?.value === 'from.chat.contains') {
            vars.push({ key: 'trigger.mensagem', description: t('variables.triggerMensagem', 'Mensagem que ativou o gatilho'), type: t('variables.types.trigger', 'Gatilho') });
            vars.push({ key: 'trigger.keywords', description: t('variables.triggerKeywords', 'Palavras-chave detectadas'), type: t('variables.types.trigger', 'Gatilho') });
        }
        if (actionPhase?.value === 'from.chat.feedback') {
            vars.push({ key: 'trigger.feedback', description: t('variables.triggerFeedback', 'Feedback recebido'), type: t('variables.types.trigger', 'Gatilho') });
            vars.push({ key: 'trigger.nota', description: t('variables.triggerNota', 'Nota do feedback'), type: t('variables.types.trigger', 'Gatilho') });
        }

        return vars;
    }, [actionPhase, t]);

    // --- Data Fetching ---
    const fetchAuxData = async () => {
        try {
            const [channelsRes, usersRes] = await Promise.all([
                actionListaCanaisPorCliente(user?.clienteId),
                getActionListaUsuariosByClienteId(user?.clienteId)
            ]);
            setCanais(channelsRes.data.data || []);
            setUsers(usersRes.data.usuarios || []);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (user?.clienteId) fetchAuxData();
    }, [user?.clienteId]);

    useEffect(() => {
        const fetchAutomation = async () => {
            try {
                const response = await v2_actionAutomacao(id);
                const data = response.data.data;
                setAutomacao(data);

                // Parse "From" (Trigger)
                if (data?.params?.from) {
                    const eventDef = automationEvents.find(e => e.value === data.params.from.action);
                    setActionPhase(eventDef);
                    // Ensure defaults
                    const safeActionState = { ...data.params.from };
                    if (!safeActionState.schedule) {
                        safeActionState.schedule = diasSemana.map(({ dia, numeroDia }) => ({
                            dia, numeroDia, ativo: true, inicio: "08:00", fim: "18:00"
                        }));
                    }
                    setActionState(safeActionState);
                }

                // Parse "To" (Actions)
                if (data?.params?.to) {
                    const mappedBlocks = data.params.to.map(b => ({
                        id: b.id || uuidv4(),
                        value: b.value,
                        data: b.data || {}
                    }));
                    setBlocks(mappedBlocks);
                }

            } catch (error) {
                console.error("Erro ao carregar automação", error);
                enqueueSnackbar(t('workflow.toasts.loadError', 'Erro ao carregar automação'), { variant: 'error' });
            }
        };

        if (id) fetchAutomation();
    }, [id, enqueueSnackbar]);


    // --- Functions (Defined before useEffect to be used inside) ---
    const addBlock = (value = null) => {
        if (!actionPhase) {
            // Setting the Trigger
            const selectedTrigger = automationEvents.find(e => e.value === value);
            if (selectedTrigger) {
                setActionPhase(selectedTrigger);
                // Initialize default state for the trigger
                setActionState({
                    pipelineId: null, stageId: null, pipelineIdTarget: null, stageIdTarget: null, valuesFields: [],
                    chatUsersAccept: [], chatKeywords: [], cardTransferUsers: [], chatDepartamentsAssign: [], chatUsersAssign: [],
                    timeInativity: 5, inactivityType: "cliente", timeUnitInativity: 1, stage_ids: [], chatStatus: [],
                    tags: [], actionTag: null, ignorePreviousActivities: true, executeOnlyTime: true,
                    schedule: diasSemana.map(({ dia, numeroDia }) => ({ dia, numeroDia, ativo: true, inicio: "08:00", fim: "18:00" })),
                    scheduleCustom: false, feedbacks: [], aiDetect: false, aiDetectContext: "", detectTrigger: false,
                    detectTypeMsg: false, detectTypeMsgTypes: [], limitMsgAgent: null, limitMsgClient: null,
                    chatClientId: null, periodSendResume: null, aiAnalysisSource: "cliente", aiIntelligenceLevel: "basico",
                    campaignId: null, chatChannels: [], linkToChatUser: null, chatTags: [],
                    executeValidityValue: null, executeValidityUnit: null, isExecuteValidity: false
                });
            }
            return;
        }

        setBlocks((prevBlocks) => [
            ...prevBlocks,
            {
                id: uuidv4(),
                value: value,
                data: null
            },
        ]);
    };

    const removeBlock = (id) => {
        setBlocks(prev => prev.filter((block) => block.id !== id));
        if (selectedNodeId === id) {
            setOpenConfig(false);
            setSelectedNodeId(null);
        }
    };

    const updateBlockType = (id, value) => {
        setBlocks(prev =>
            prev.map((block) => (block.id === id ? { ...block, value, data: {} } : block))
        );
    };

    // --- Sync Nodes/Edges from State to React Flow ---
    useEffect(() => {
        if (!actionPhase) {
            setNodes([]);
            setEdges([]);
            return;
        }

        const newNodes = [];
        const newEdges = [];

        // Layout Constants
        const startX = 100;
        const startY = 50;
        const nodeSpacingY = 160;
        const firstNodeSpacing = 180;
        const branchOffsetX = 180;
        const branchStartOffsetY = 160;
        const convergeGapY = 60;
        const nodeWidth = 240;

        // 1. Trigger Node
        const triggerNodeId = 'trigger';
        newNodes.push({
            id: triggerNodeId,
            type: 'n8nNode',
            position: { x: startX, y: startY },
            data: {
                label: actionPhase.name,
                subLabel: t('workflow.node.triggerSubLabel', 'Gatilho'),
                icon: actionPhase.icon,
                isTrigger: true,
                colorClass: 'bg-emerald-100 text-emerald-600',
                description: t('workflow.node.clickToConfigure', 'Clique para configurar'),
                ...(blocks.length === 0 && {
                    onDelete: () => {
                        setActionPhase(null);
                        setActionState({});
                        setOpenConfig(false);
                        setSelectedNodeId(null);
                    }
                })
            },
            draggable: false,
        });

        // 2. Action Nodes
        let lastMainNodeId = triggerNodeId;
        let currentY = startY + firstNodeSpacing;

        blocks.forEach((block) => {
            const actionDef = automationActions.find(a => a.value === block.value);
            const blockId = block.id;

            if (block.value !== 'to.condition') {
                // --- Normal (non-condition) block ---
                newNodes.push({
                    id: blockId,
                    type: 'n8nNode',
                    position: { x: startX, y: currentY },
                    data: {
                        label: block.data?.customLabel || actionDef?.name || t('workflow.node.selectAction', 'Selecione a ação'),
                        subLabel: t('workflow.node.actionSubLabel', 'Ação'),
                        icon: actionDef?.icon || 'mdi:help-circle-outline',
                        isTrigger: false,
                        colorClass: block.value ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500',
                        description: block.value ? t('workflow.node.clickToConfigure', 'Clique para configurar') : t('workflow.node.needsConfig', 'Necessário configurar'),
                        isIncomplete: isStepIncomplete(block, t),
                        onDelete: () => removeBlock(blockId)
                    },
                    draggable: false,
                });

                newEdges.push({
                    id: `e-${lastMainNodeId}-${blockId}`,
                    source: lastMainNodeId,
                    target: blockId,
                    animated: true,
                    style: { stroke: '#94a3b8', strokeWidth: 2 },
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' }
                });

                lastMainNodeId = blockId;
                currentY += nodeSpacingY;
            } else {
                // --- Condition block with branching ---
                const conditionCenterX = startX + nodeWidth / 2; // center of node

                newNodes.push({
                    id: blockId,
                    type: 'conditionNode',
                    position: { x: startX, y: currentY },
                    data: {
                        label: block.data?.customLabel || actionDef?.name || t('workflow.node.conditionLabel', 'Condição'),
                        subLabel: t('workflow.node.conditionLabel', 'Condição'),
                        icon: actionDef?.icon || 'mdi:source-branch',
                        description: (block.data?.conditions?.length || 0) > 0
                            ? t('workflow.condition.countLabel', { count: block.data.conditions.length, defaultValue: '{{count}} condição(ões)' })
                            : t('workflow.node.clickToConfigure', 'Clique para configurar'),
                        isIncomplete: isStepIncomplete(block, t),
                        onDelete: () => removeBlock(blockId)
                    },
                    draggable: false,
                });

                newEdges.push({
                    id: `e-${lastMainNodeId}-${blockId}`,
                    source: lastMainNodeId,
                    target: blockId,
                    animated: true,
                    style: { stroke: '#94a3b8', strokeWidth: 2 },
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' }
                });

                const truePath = block.data?.truePath || [];
                const falsePath = block.data?.falsePath || [];

                const leftX = startX - branchOffsetX;
                const rightX = startX + branchOffsetX;
                const branchStartY = currentY + branchStartOffsetY;

                // Helper to build a branch
                const buildBranch = (subBlocks, path, baseX, sourceHandle, accentColor, label) => {
                    const strokeColor = accentColor === 'green' ? '#22c55e' : '#ef4444';
                    let branchY = branchStartY;
                    let lastBranchNodeId = blockId;
                    let isFirst = true;

                    subBlocks.forEach((subBlock) => {
                        const subNodeId = `${blockId}::${path}::${subBlock.id}`;
                        const subActionDef = automationActions.find(a => a.value === subBlock.value);

                        newNodes.push({
                            id: subNodeId,
                            type: 'n8nNode',
                            position: { x: baseX, y: branchY },
                            data: {
                                label: subBlock.data?.customLabel || subActionDef?.name || t('workflow.node.selectAction', 'Selecione a ação'),
                                subLabel: label,
                                icon: subActionDef?.icon || 'mdi:help-circle-outline',
                                isTrigger: false,
                                colorClass: subBlock.value
                                    ? (accentColor === 'green' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600')
                                    : 'bg-gray-100 text-gray-500',
                                description: subBlock.value ? t('workflow.node.clickToConfigure', 'Clique para configurar') : t('workflow.node.needsConfig', 'Necessário configurar'),
                                isIncomplete: isStepIncomplete(subBlock, t),
                                onDelete: () => handleRemoveSubBlock(blockId, path, subBlock.id)
                            },
                            draggable: false,
                        });

                        newEdges.push({
                            id: `e-${lastBranchNodeId}-${subNodeId}`,
                            source: lastBranchNodeId,
                            sourceHandle: isFirst ? sourceHandle : undefined,
                            target: subNodeId,
                            type: 'default',
                            animated: true,
                            style: { stroke: strokeColor, strokeWidth: 2 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: strokeColor },
                        });

                        lastBranchNodeId = subNodeId;
                        isFirst = false;
                        branchY += nodeSpacingY;
                    });

                    // Add button at end of branch
                    const addNodeId = `add-${blockId}-${path}`;
                    newNodes.push({
                        id: addNodeId,
                        type: 'addNode',
                        position: { x: baseX + nodeWidth / 2 - 20, y: branchY },
                        data: {
                            accentColor,
                            conditionBlockId: blockId,
                            path,
                        },
                        draggable: false,
                    });

                    newEdges.push({
                        id: `e-${lastBranchNodeId}-${addNodeId}`,
                        source: lastBranchNodeId,
                        sourceHandle: isFirst ? sourceHandle : undefined,
                        target: addNodeId,
                        type: 'smoothstep',
                        animated: false,
                        style: { stroke: strokeColor, strokeWidth: 1.5, strokeDasharray: '5 3' },
                    });

                    return { lastNodeId: addNodeId, endY: branchY + 60 };
                };

                const trueResult = buildBranch(truePath, 'truePath', leftX, 'true-out', 'green', t('workflow.branch.true', 'Se'));
                const falseResult = buildBranch(falsePath, 'falsePath', rightX, 'false-out', 'red', t('workflow.branch.false', 'Senão'));

                // Converge node
                const convergeY = Math.max(trueResult.endY, falseResult.endY) + convergeGapY;
                const convergeId = `converge-${blockId}`;

                newNodes.push({
                    id: convergeId,
                    type: 'convergeNode',
                    position: { x: startX + nodeWidth / 2 - 12, y: convergeY },
                    data: {},
                    draggable: false,
                });

                // Edges from branch ends to converge
                newEdges.push({
                    id: `e-${trueResult.lastNodeId}-${convergeId}`,
                    source: trueResult.lastNodeId,
                    target: convergeId,
                    type: 'smoothstep',
                    animated: false,
                    style: { stroke: '#cbd5e1', strokeWidth: 1.5, strokeDasharray: '4 3' },
                });
                newEdges.push({
                    id: `e-${falseResult.lastNodeId}-${convergeId}`,
                    source: falseResult.lastNodeId,
                    target: convergeId,
                    type: 'smoothstep',
                    animated: false,
                    style: { stroke: '#cbd5e1', strokeWidth: 1.5, strokeDasharray: '4 3' },
                });

                lastMainNodeId = convergeId;
                currentY = convergeY + 80;
            }
        });

        // 3. Add Button Node (main flow terminator)
        if (isActionValid) {
            const addNodeId = 'add-node-button';
            newNodes.push({
                id: addNodeId,
                type: 'addNode',
                position: { x: startX + nodeWidth / 2 - 20, y: currentY },
                data: {
                    onClick: () => addBlock(null)
                },
                draggable: false
            });

            newEdges.push({
                id: `e-${lastMainNodeId}-${addNodeId}`,
                source: lastMainNodeId,
                target: addNodeId,
                animated: true,
                style: { stroke: '#cbd5e1', strokeWidth: 2, strokeDasharray: 5 },
            });
        }

        setNodes(newNodes);
        setEdges(newEdges);

    }, [actionPhase, blocks, setNodes, setEdges, isActionValid]);




    const onNodeClick = (event, node) => {
        if (node.type === 'addNode') {
            if (node.data.conditionBlockId && node.data.path) {
                handleAddSubBlock(node.data.conditionBlockId, node.data.path);
            } else if (node.data?.onClick) {
                node.data.onClick();
            }
            return;
        }
        if (node.type === 'convergeNode') return;
        setSelectedNodeId(node.id);
        setOpenConfig(true);
    };

    const deleteAutomacao = async (automacaoId) => {
        try {
            setLoadingDelete(true);
            await v2_actionDeletarAutomacao(automacaoId);
            enqueueSnackbar(t('workflow.toasts.deleteSuccess', 'Automação excluída com sucesso'), { variant: "success" });
            navigate(-1);
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('shared.toasts.deleteError', 'Erro ao excluir automação'), { variant: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleSave = async () => {
        if (!actionPhase) return;

        // Validation: Check empty blocks
        if (blocks.some(b => !b.value)) {
            enqueueSnackbar(t('workflow.toasts.incompleteBlocks', 'Configure todas as ações antes de salvar.'), { variant: "error" });
            return;
        }

        const blockWithInvalidChannel = blocks.find(block =>
            block.value === "to.chat.sendMessage" &&
            block.data?.useChannel === true &&
            !block.data?.chatChannel
        );

        if (blockWithInvalidChannel) {
            enqueueSnackbar(t('shared.toasts.channelRequired', "Selecione um canal de envio quando a opção 'Selecionar canal específico' estiver ativada"), { variant: "error" });
            return;
        }

        const payload = {
            scope: {
                status: automacao?.status,
                name: automacao?.name,
                params: {
                    from: {
                        action: actionPhase.value,
                        ...actionState
                    },
                    to: blocks.map(b => ({ id: b.id, value: b.value, data: b.data }))
                }
            }
        };

        try {
            await v2_actionEditarAutomacao(id, payload);
            enqueueSnackbar(t('workflow.toasts.saveSuccess', 'Fluxo salvo com sucesso!'), { variant: 'success' });
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('toast.saveError', { ns: 'notifications', defaultValue: 'Erro ao salvar' }), { variant: 'error' });
        }
    };

    const handleChangeAction = (event) => {
        const { name, value } = event;
        if (name === 'detectTypeMsgTypes' && value) {
            setActionState(prev => ({ ...prev, [name]: value, aiDetect: false }));
            return;
        }
        setActionState(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeBlock = (blockId, event) => {
        const { name, value, action } = event;
        setBlocks(prevBlocks => prevBlocks.map(block => {
            if (block.id !== blockId) return block;
            if (name === 'chatFiles') {
                let updatedFiles;
                if (action === 'delete') {
                    updatedFiles = value;
                } else {
                    updatedFiles = [...(block.data?.chatFiles || []), ...(Array.isArray(value) ? value : [value])];
                }
                return { ...block, data: { ...block.data, [name]: updatedFiles } };
            }
            return { ...block, data: { ...block.data, [name]: value } };
        }));
    };

    // --- Sub-block management for condition branches ---
    const handleAddSubBlock = (conditionBlockId, path) => {
        setBlocks(prev => prev.map(block => {
            if (block.id !== conditionBlockId) return block;
            const current = block.data?.[path] || [];
            return { ...block, data: { ...block.data, [path]: [...current, { id: uuidv4(), value: null, data: {} }] } };
        }));
    };

    const handleRemoveSubBlock = (conditionBlockId, path, subBlockId) => {
        setBlocks(prev => prev.map(block => {
            if (block.id !== conditionBlockId) return block;
            const current = block.data?.[path] || [];
            return { ...block, data: { ...block.data, [path]: current.filter(b => b.id !== subBlockId) } };
        }));
        if (selectedNodeId === `${conditionBlockId}::${path}::${subBlockId}`) {
            setOpenConfig(false);
            setSelectedNodeId(null);
        }
    };

    const handleUpdateSubBlockType = (conditionBlockId, path, subBlockId, value) => {
        setBlocks(prev => prev.map(block => {
            if (block.id !== conditionBlockId) return block;
            const current = block.data?.[path] || [];
            return { ...block, data: { ...block.data, [path]: current.map(b => b.id === subBlockId ? { ...b, value, data: {} } : b) } };
        }));
    };

    const handleChangeSubBlock = (conditionBlockId, path, subBlockId, event) => {
        setBlocks(prev => prev.map(block => {
            if (block.id !== conditionBlockId) return block;
            const current = block.data?.[path] || [];
            if (event.name === 'chatFiles') {
                const subBlock = current.find(b => b.id === subBlockId);
                let updatedChatFiles;
                if (event.action === 'delete') {
                    updatedChatFiles = event.value;
                } else {
                    updatedChatFiles = [...(subBlock?.data?.chatFiles || []), ...(Array.isArray(event.value) ? event.value : [event.value])];
                }
                return { ...block, data: { ...block.data, [path]: current.map(b => b.id === subBlockId ? { ...b, data: { ...b.data, chatFiles: updatedChatFiles } } : b) } };
            }
            return { ...block, data: { ...block.data, [path]: current.map(b => b.id === subBlockId ? { ...b, data: { ...b.data, [event.name]: event.value } } : b) } };
        }));
    };

    // --- Render Configuration Content ---
    const renderConfigContent = () => {
        if (!selectedNodeId) return null;

        // Trigger Config
        if (selectedNodeId === 'trigger') {
            if (!actionPhase) return <div className="dark:text-[#ABABAD]">{t('buttons.loading', { ns: 'common', defaultValue: 'Carregando...' })}</div>;
            return (
                <div className="space-y-4">
                    <h2 className="text-lg font-bold flex items-center gap-2 dark:text-[#D1D2D3]">
                        <Iconify icon={actionPhase.icon} className="text-emerald-600" />
                        {actionPhase.name}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-[#ABABAD] mb-4">{t('workflow.trigger.subtitle', 'Configurações do gatilho inicial.')}</p>
                    {actionPhase.value === "from.funnel.card.in.stage" && <CardActionEntrarFase users={usersFormatted} actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} />}
                    {actionPhase.value === "from.funnel.card.out.stage" && <CardActionSairEstagio users={usersFormatted} actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} />}
                    {actionPhase.value === "from.chat.finish" && <CardChatFinish users={usersFormatted} actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} />}
                    {actionPhase.value === "from.funnel.card.inOut.stage" && <CardActionInOutFase users={usersFormatted} actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} />}
                    {actionPhase.value === "from.funnel.card.create" && <LabelFromAction>{actionPhase.name}</LabelFromAction>}
                    {actionPhase.value === "from.jobs.birthdate" && <LabelFromAction>{actionPhase.name}</LabelFromAction>}
                    {actionPhase.value === "from.chat.firstMessage" && <CardChatFirstMessage users={usersFormatted} actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} />}
                    {actionPhase.value === "from.funnel.card.field.change" && <CardActionCampoAtualizado users={usersFormatted} actionState={actionState} changeActionState={handleChangeAction} cliente={cliente} actionPhase={actionPhase} />}
                    {actionPhase.value === "from.chat.accept" && <ChatAccept users={usersFormatted} actionPhase={actionPhase} user={user} changeActionState={handleChangeAction} actionState={actionState} />}
                    {actionPhase.value === "from.chat.tag.inOut" && <CardChatTagInOut actionPhase={actionPhase} user={user} changeActionState={handleChangeAction} actionState={actionState} users={usersFormatted} />}
                    {actionPhase.value === "from.chat.assign" && <ChatAssign actionPhase={actionPhase} user={user} changeActionState={handleChangeAction} actionState={actionState} />}
                    {actionPhase.value === "from.chat.contains" && <ChatKeywordFilter actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} user={user} cliente={cliente} />}
                    {actionPhase.value === "from.chat.feedback" && <CardChatFeedback actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} user={user} users={usersFormatted} />}
                    {actionPhase.value === "from.funnel.card.inativity" && <CardInatividadeFunil actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} diasSemana={diasSemana} />}
                    {actionPhase.value === "from.chat.inactivity" && <CardInatividadeChat user={user} actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} diasSemana={diasSemana} />}
                    {actionPhase.value === "from.chats.resume" && <CardResumoConversa actionPhase={actionPhase} actionState={actionState} changeActionState={handleChangeAction} user={user} diasSemana={diasSemana} />}
                </div>
            );
        }

        // Sub-block Config (condition branch nodes with :: in ID)
        if (selectedNodeId.includes('::')) {
            const [conditionBlockId, path, subBlockId] = selectedNodeId.split('::');
            const condBlock = blocks.find(b => b.id === conditionBlockId);
            const subBlock = condBlock?.data?.[path]?.find(b => b.id === subBlockId);

            if (subBlock) {
                const subBlockActions = automationActions.filter(a => a.value !== 'to.condition' && a.accepts.includes(actionPhase?.value));
                const groupedSubActions = subBlockActions.reduce((groups, action) => {
                    if (!groups[action.category]) groups[action.category] = [];
                    groups[action.category].push(action);
                    return groups;
                }, {});

                return (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center bg-slate-100 dark:bg-[#1A1D21] p-3 rounded-lg border border-slate-200 dark:border-[#383838]">
                            <div className="flex-1 mr-4">
                                <span className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] mb-1 block">
                                    {t('workflow.branch.actionLabel', { branch: path === 'truePath' ? t('workflow.branch.true', 'Se') : t('workflow.branch.false', 'Senão'), defaultValue: 'Ação do Branch ({{branch}})' })}
                                </span>
                                <Select
                                    onValueChange={(value) => handleUpdateSubBlockType(conditionBlockId, path, subBlockId, value)}
                                    value={subBlock.value || ""}
                                >
                                    <SelectTrigger className="bg-white dark:bg-[#222529] dark:border-[#383838] dark:text-[#D1D2D3]">
                                        <SelectValue placeholder={t('workflow.action.selectEllipsis', 'Selecione...')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(groupedSubActions).map((category, index) => (
                                            <div key={index}>
                                                <h3 className="font-bold text-sm text-gray-700 dark:text-[#D1D2D3] capitalize px-2 py-1">{category}</h3>
                                                {groupedSubActions[category].map((event, eventIndex) => (
                                                    <SelectItem key={eventIndex} value={event.value}>
                                                        <div className="flex items-center gap-2">
                                                            <Iconify icon={event.icon} className="mr-2" />
                                                            {event.name}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </div>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                variant="ghost"
                                className="bg-red-100 dark:bg-red-500/10 text-red-600 hover:bg-red-200 dark:hover:bg-red-500/20 hover:text-red-700 h-10 w-10 p-0"
                                onClick={() => handleRemoveSubBlock(conditionBlockId, path, subBlockId)}
                                title={t('workflow.action.removeTitle', 'Remover ação')}
                            >
                                <Iconify icon="mdi:delete-outline" />
                            </Button>
                        </div>

                        <div className="bg-white dark:bg-[#222529] p-3 rounded-lg border border-slate-200 dark:border-[#383838]">
                            <label className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] mb-1 block">{t('workflow.action.blockNameLabel', 'Nome do Bloco')}</label>
                            <Input
                                value={subBlock.data?.customLabel || ''}
                                onChange={(e) => {
                                    handleChangeSubBlock(conditionBlockId, path, subBlockId, { name: 'customLabel', value: e.target.value });
                                }}
                                placeholder={automationActions.find(a => a.value === subBlock.value)?.name || t('workflow.action.blockNamePlaceholder', 'Nome do bloco')}
                                className="bg-white dark:bg-[#222529] border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3] text-[0.9rem]"
                            />
                        </div>

                        {subBlock.value && (
                            <div className="border border-slate-200 dark:border-[#383838] rounded-lg overflow-hidden mt-6 shadow-sm">
                                <div className="bg-slate-50 dark:bg-[#1A1D21] p-4 border-b border-slate-200 dark:border-[#383838] flex items-center gap-2">
                                    <div className={`p-1.5 ${path === 'truePath' ? 'bg-green-100 dark:bg-green-500/10 text-green-600' : 'bg-red-100 dark:bg-red-500/10 text-red-600'} rounded`}>
                                        <Iconify icon="mdi:cog" width={18} height={18} />
                                    </div>
                                    <h2 className="text-sm font-bold text-slate-700 dark:text-[#D1D2D3] uppercase tracking-wide">
                                        {t('workflow.action.configureLabel', 'Configurar:')} <span className={path === 'truePath' ? 'text-green-600' : 'text-red-600'}>{automationActions.find(a => a.value === subBlock.value)?.name || '...'}</span>
                                    </h2>
                                </div>
                                <div className="p-6 bg-white dark:bg-[#222529]">
                                    <BlockRenderer
                                        block={subBlock}
                                        changeBlock={(event) => handleChangeSubBlock(conditionBlockId, path, subBlockId, event)}
                                        availableVariables={availableVariables}
                                        actionPhase={actionPhase}
                                        actionState={actionState}
                                        canais={canais}
                                        user={user}
                                    />
                                </div>
                            </div>
                        )}

                        {!subBlock.value && (
                            <div className="text-center py-12 px-4">
                                <div className="w-16 h-16 bg-slate-50 dark:bg-[#1A1D21] rounded-full flex items-center justify-center mx-auto mb-3">
                                    <Iconify icon="mdi:gesture-tap" className="text-slate-300 dark:text-[#ABABAD] text-3xl" />
                                </div>
                                <h3 className="text-slate-600 dark:text-[#ABABAD] font-medium mb-1">{t('workflow.action.noneSelectedTitle', 'Nenhuma ação selecionada')}</h3>
                                <p className="text-slate-400 dark:text-[#ABABAD] text-sm">{t('workflow.action.noneSelectedDescription', 'Selecione um tipo de ação no painel acima para configurar os detalhes.')}</p>
                            </div>
                        )}
                    </div>
                );
            }
        }

        // Action Config
        const block = blocks.find(b => b.id === selectedNodeId);
        if (block) {
            return (
                <div className="space-y-4">
                    <div className="flex justify-between items-center bg-slate-100 dark:bg-[#1A1D21] p-3 rounded-lg border border-slate-200 dark:border-[#383838]">
                        <div className="flex-1 mr-4">
                            <span className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] mb-1 block">{t('workflow.action.typeLabel', 'Tipo de Ação')}</span>
                            <Select
                                onValueChange={(value) => updateBlockType(block.id, value)}
                                value={block.value || ""}
                            >
                                <SelectTrigger className="bg-white dark:bg-[#222529] dark:border-[#383838] dark:text-[#D1D2D3]">
                                    <SelectValue placeholder={t('workflow.action.selectEllipsis', 'Selecione...')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.keys(groupedActions).map((category, index) => (
                                        <div key={index}>
                                            <h3 className="font-bold text-sm text-gray-700 dark:text-[#D1D2D3] capitalize px-2 py-1">{category}</h3>
                                            {groupedActions[category].map((event, eventIndex) => (
                                                <SelectItem key={eventIndex} value={event.value}>
                                                    <div className="flex items-center gap-2">
                                                        <Iconify icon={event.icon} className="mr-2" />
                                                        {event.name}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </div>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            variant="ghost"
                            className="bg-red-100 dark:bg-red-500/10 text-red-600 hover:bg-red-200 dark:hover:bg-red-500/20 hover:text-red-700 h-10 w-10 p-0"
                            onClick={() => removeBlock(block.id)}
                            title={t('workflow.action.removeTitle', 'Remover ação')}
                        >
                            <Iconify icon="mdi:delete-outline" />
                        </Button>
                    </div>

                    <div className="bg-white dark:bg-[#222529] p-3 rounded-lg border border-slate-200 dark:border-[#383838]">
                        <label className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] mb-1 block">{t('workflow.action.blockNameLabel', 'Nome do Bloco')}</label>
                        <Input
                            value={block.data?.customLabel || ''}
                            onChange={(e) => {
                                handleChangeBlock(block.id, { name: 'customLabel', value: e.target.value });
                            }}
                            placeholder={automationActions.find(a => a.value === block.value)?.name || t('workflow.action.blockNamePlaceholder', 'Nome do bloco')}
                            className="bg-white dark:bg-[#222529] border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3] text-[0.9rem]"
                        />
                    </div>

                    <div className="border border-slate-200 dark:border-[#383838] rounded-lg overflow-hidden mt-6 shadow-sm">
                        <div className="bg-slate-50 dark:bg-[#1A1D21] p-4 border-b border-slate-200 dark:border-[#383838] flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-500/10 rounded text-blue-600">
                                <Iconify icon="mdi:cog" width={18} height={18} />
                            </div>
                            <h2 className="text-sm font-bold text-slate-700 dark:text-[#D1D2D3] uppercase tracking-wide">
                                {t('workflow.action.configureLabel', 'Configurar:')} <span className="text-blue-600">{automationActions.find(a => a.value === block.value)?.name || '...'}</span>
                            </h2>
                        </div>
                        <div className="p-6 bg-white dark:bg-[#222529]">
                            {block.value === "to.funnel.cardTransferUser" && <CardTransferCard user={user} block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.addNote" && <ChatAddNoteComponent block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.httpRequest" && <HttpRequestComponent block={block} availableVariables={availableVariables} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.sendMessage" && <CardSendMessage block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} actionState={actionState} actionPhase={actionPhase} canais={canais} />}
                            {block.value === "to.chat.addTag" && <ChatAddTag block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.removeTag" && <ChatRemoveTag block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.funnel.cardCreate" && <CardNewLead block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.moveCardStage" && <CardReactionMoverCardFunil block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.funnel.action" && <CardChangeValue block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.sendMake" && <CardSendMake block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.finish" && <CardFinishChat block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.enableBot" && <CardEnableBot block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.transfer" && <CardTransferChat user={user} block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.chat.assign" && <CardAssignChat user={user} block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}
                            {block.value === "to.condition" && <CardCondition block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} availableVariables={availableVariables} actionPhase={actionPhase} actionState={actionState} canais={canais} user={user} hideSubBlocks />}
                            {block.value === "to.wait" && <CardWait block={block} changeBlock={(e) => handleChangeBlock(block.id, e)} />}

                            {!block.value && (
                                <div className="text-center py-12 px-4">
                                    <div className="w-16 h-16 bg-slate-50 dark:bg-[#1A1D21] rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Iconify icon="mdi:gesture-tap" className="text-slate-300 dark:text-[#ABABAD] text-3xl" />
                                    </div>
                                    <h3 className="text-slate-600 dark:text-[#ABABAD] font-medium mb-1">{t('workflow.action.noneSelectedTitle', 'Nenhuma ação selecionada')}</h3>
                                    <p className="text-slate-400 dark:text-[#ABABAD] text-sm">{t('workflow.action.noneSelectedDescription', 'Selecione um tipo de ação no painel acima para configurar os detalhes.')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return <div className="dark:text-[#ABABAD]">{t('workflow.selectNodePrompt', 'Selecione um nó para configurar.')}</div>;
    };

    const nodeTypes = useMemo(() => ({ n8nNode: N8nNode, addNode: AddNode, conditionNode: ConditionNode, convergeNode: ConvergeNode }), []);

    return (
        <>
        <ModalConfimacao
            open={openModalConfirmacao}
            loading={loadingDelete}
            onClose={() => {
                setOpenModalConfirmacao(false);
            }}
            onConfirm={async () => {
                try {
                    await deleteAutomacao(id);
                    setOpenModalConfirmacao(false);
                } catch (err) {
                    console.log(err);
                }
            }}
            loadingFinalizar={false}
            mensagem={t('shared.confirmDelete', 'Deseja mesmo excluir?')}
        />

        <div className={D.editorRoot} style={{ fontFamily: D.fontStack }}>
            {/* Header */}
            <div className={D.editorHeader}>
                {/* Esquerda: Voltar + Nome */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <button type="button" className={D.editorBack} onClick={() => navigate('/dashboard/automacoes')}>
                        <Iconify icon="mdi:arrow-left" width={18} />
                    </button>
                    <div className="flex flex-col min-w-0">
                        <input
                            type="text"
                            value={automacao?.name || ''}
                            onChange={(e) => setAutomacao(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('shared.namePlaceholder', 'Nome da automação')}
                            className={D.editorNameInput}
                        />
                        <span className={D.editorMeta}>{t('shared.automationLabel', 'Automação')}</span>
                    </div>
                </div>

                {/* Centro: Toggle de visualização */}
                <div className={D.viewToggle}>
                    <button
                        type="button"
                        onClick={() => navigate(`/dashboard/automacoes/${id}`)}
                        className={D.viewToggleBtn}
                    >
                        <Iconify icon="mdi:view-list-outline" width={15} />
                        {t('shared.viewToggle.classic', 'Clássico')}
                    </button>
                    <button type="button" className={D.viewToggleBtnActive}>
                        <Iconify icon="mdi:graph-outline" width={15} />
                        {t('shared.viewToggle.workflow', 'Workflow')}
                    </button>
                </div>

                {/* Direita: Status + Data + Ações */}
                <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={automacao?.status}
                            className={D.switch}
                            onClick={(event) => {
                                event.stopPropagation();
                                setAutomacao(prev => ({ ...prev, status: !prev.status }));
                            }}
                        />
                        {automacao?.status ? (
                            <span className="text-[12px] font-semibold text-[#067647]">{t('status.active', { ns: 'common', defaultValue: 'Ativo' })}</span>
                        ) : (
                            <span className="text-[12px] font-semibold text-[#a1a1aa]">{t('status.inactive', { ns: 'common', defaultValue: 'Inativo' })}</span>
                        )}
                    </div>

                    {automacao?.create_at && (
                        <>
                            <div className={D.editorDivider} />
                            <span className={`${D.editorMeta} whitespace-nowrap`}>{fDateTime(automacao.create_at)}</span>
                        </>
                    )}

                    <div className={D.editorDivider} />

                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            className={D.iconButtonDanger}
                            onClick={() => setOpenModalConfirmacao(true)}
                        >
                            <Iconify icon="mdi:delete-outline" width={18} />
                        </button>
                        <DialogLogsAutomacao
                            automationId={id}
                            user={user}
                        />
                    </div>

                    <button type="button" className={D.primaryButton} onClick={handleSave}>
                        {t('buttons.save', { ns: 'common', defaultValue: 'Salvar' })}
                    </button>
                </div>
            </div>


            <div className="flex-1 w-full h-full relative flex overflow-hidden">
                <Sidebar actions={sidebarItems} onAddBlock={addBlock} title={sidebarTitle} />

                <div className="flex-1 h-full relative">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={nodeTypes}
                        onNodeClick={onNodeClick}
                        fitView
                        className="bg-[#f4f4f7]"
                    >
                        <Background color="#d4d4d8" gap={20} size={1} variant="dots" />
                        <Controls className="bg-white shadow-none rounded-lg border border-[#e7e7ec] p-1" />
                    </ReactFlow>

                    {!actionPhase && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                            <div className="flex items-center gap-3 bg-white border border-[#e7e7ec] rounded-[12px] px-6 py-4">
                                <Iconify icon="mdi:arrow-left" width={20} className="text-[#6d28d9] animate-pulse" />
                                <div>
                                    <p className="text-[14px] font-semibold text-[#18181b]">{t('workflow.emptyCanvas.title', 'Selecione um gatilho')}</p>
                                    <p className="text-[12px] text-[#71717a]">{t('workflow.emptyCanvas.description', 'Escolha um evento no painel ao lado para começar')}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </div>

            {/* Config Drawer (right side) - rendered at root level for proper z-index/overflow */}
            {openConfig && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[9998] animate-in fade-in duration-200"
                        onClick={() => setOpenConfig(false)}
                    />
                    <div className="fixed top-0 right-0 h-full w-[780px] max-w-[90vw] bg-white border-l border-[#e7e7ec] z-[9999] flex flex-col animate-in slide-in-from-right duration-300">
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-[#383838] flex items-center justify-between shrink-0">
                            <h2 className="font-bold text-sm text-slate-800 dark:text-[#D1D2D3]">{t('workflow.configDrawer.title', 'Configuração')}</h2>
                            <button
                                onClick={() => setOpenConfig(false)}
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#383838] rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:text-[#ABABAD]"
                            >
                                <Iconify icon="mdi:close" width={18} />
                            </button>
                        </div>
                        {/* Content */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="p-5">
                                {renderConfigContent()}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
        </>
    );
}

